/*
 * ----------------------------------------------------------------------------
 * CUSTOM WISHLIST (Swym Wishlist Plus JS SDK)
 * ----------------------------------------------------------------------------
 *
 * Theme-native wishlist UI backed by the Swym JS SDK. The Swym "Wishlist App
 * Embed" loads the SDK and exposes it via window.SwymCallbacks, so no API key
 * is ever present in the storefront.
 *
 * Product-level wishlist: items are matched by product id (empi). The first
 * available variant id (epi) is sent because Swym keys items on the variant.
 *
 * Custom elements:
 *   <wishlist-button>  toggle a single product, reflects state via aria-pressed
 *   <wishlist-count>   header badge mirroring the number of saved products
 *   <wishlist-list>    drawer body, toggles empty-state vs rendered item rows
 */
(function () {
  'use strict';

  // Surfaces wishlist failures to the console (red error) and, when available,
  // to PostHog as a "wishlist_error" event so they can be tracked.
  function reportError(context, error, extra) {
    var detail = error instanceof Error ? error : new Error(String(error || context));
    console.error('[wishlist] ' + context, detail, extra || {});
    var ph = window.posthog;
    if (ph && typeof ph.capture === 'function') {
      try {
        var props = { context: context, message: detail.message };
        if (extra) {
          Object.keys(extra).forEach(function (key) {
            props[key] = extra[key];
          });
        }
        ph.capture('wishlist_error', props);
      } catch (telemetryError) {
        // Telemetry must never break the UI.
      }
    }
  }

  // Quiet, non-error trace for expected states (e.g. the SDK not loading because
  // the shopper has not accepted cookies). Never sent to PostHog.
  function logDebug(context, message) {
    if (typeof console !== 'undefined' && typeof console.debug === 'function') {
      console.debug('[wishlist] ' + context, message || '');
    }
  }

  // Reads Shopify's Customer Privacy API to tell whether the shopper has already
  // granted the consent the wishlist needs (functional / preferences cookies,
  // which gate the Swym SDK). Returns true or false, or null when the answer is
  // not yet known (API not loaded). The cookie notice must never ask a shopper
  // who has already consented to accept cookies again.
  function wishlistConsentGranted() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) {
        return null;
      }
      if (typeof cp.preferencesProcessingAllowed === 'function') {
        return cp.preferencesProcessingAllowed();
      }
      if (typeof cp.userCanBeTracked === 'function') {
        return cp.userCanBeTracked();
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  // Reads the shopper's choice straight from the Pandectes CMP cookie. This is
  // the fallback for when a content blocker stops Shopify's consent-tracking-api
  // script: Pandectes still stores the choice, but Shopify.customerPrivacy stays
  // a stub. The cookie is base64 JSON whose `preferences` field is a bitmask of
  // DECLINED categories (1 functional, 2 performance, 4 targeting). Functional
  // cookies are the ones Pandectes maps to Shopify's "preferences" consent,
  // which gates the Swym SDK. Returns true or false, or null when no readable
  // choice is stored.
  var PANDECTES_COOKIE = '_pandectes_gdpr';
  var PANDECTES_FUNCTIONAL_DECLINED_BIT = 1;

  function pandectesFunctionalConsent() {
    try {
      var prefix = PANDECTES_COOKIE + '=';
      var entry = String(document.cookie || '')
        .split(/;\s*/)
        .filter(function (part) {
          return part.indexOf(prefix) === 0;
        })[0];
      if (!entry) {
        return null;
      }
      var stored = JSON.parse(atob(decodeURIComponent(entry.slice(prefix.length))));
      if (!stored || typeof stored.preferences !== 'number') {
        return null;
      }
      return (stored.preferences & PANDECTES_FUNCTIONAL_DECLINED_BIT) === 0;
    } catch (error) {
      return null;
    }
  }

  // Decides which drawer body to show. Returns 'items', 'empty', 'consent' or
  // 'blocked'. Consent counts as granted when Shopify says so, or when the
  // Shopify API is unavailable and Pandectes says so; an explicit Shopify "no"
  // wins over a stale Pandectes cookie. A shopper who has consented never sees
  // the cookie notice: while the SDK may still be loading they see the empty
  // state, and once the boot timeout has passed without an SDK they are told
  // that a blocker stops the wishlist, because accepting cookies again would
  // not help them.
  function drawerState(input) {
    if (input.ready) {
      return input.hasItems ? 'items' : 'empty';
    }
    var consented =
      input.shopifyConsent === true ||
      (input.shopifyConsent === null && input.pandectesConsent === true);
    if (!consented) {
      return 'consent';
    }
    return input.sdkTimedOut ? 'blocked' : 'empty';
  }

  // When the Customer Privacy API has not loaded at connect time (it emits no
  // ready event and visitorConsentCollected only fires on a fresh choice), the
  // drawer re-checks consent on this cadence. The total window (attempts *
  // interval) spans the 8s SDK boot timeout below, so an already-consented
  // returning visitor clears the cookie notice even if the SDK never boots.
  var CONSENT_RECHECK_ATTEMPTS = 16;
  var CONSENT_RECHECK_INTERVAL_MS = 500;
  var SDK_BOOT_TIMEOUT_MS = 8000;

  // Notification config, rendered inline in theme.liquid from the theme settings
  // (visibility per action, duration, texts). Safe fallbacks keep the toasts
  // working even if the config block is absent.
  var WISHLIST_CONFIG = window.ZahnheldWishlistConfig || {};

  // Transient save / remove toasts. The region is created lazily and appended to
  // <body> once, so it survives cart/section re-renders. Styling lives in
  // assets/wishlist.css (modeled on the discount-preview toast).
  var WishlistNotifications = {
    _region: null,

    // Build the info glyph with the SVG DOM API (no innerHTML) so it inherits
    // the toast's currentColor and stays XSS-safe.
    infoIcon: function () {
      var NS = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      var ring = document.createElementNS(NS, 'circle');
      ring.setAttribute('cx', '12');
      ring.setAttribute('cy', '12');
      ring.setAttribute('r', '9');
      ring.setAttribute('stroke', 'currentColor');
      ring.setAttribute('stroke-width', '1.6');
      svg.appendChild(ring);
      var stem = document.createElementNS(NS, 'path');
      stem.setAttribute('d', 'M12 11v5');
      stem.setAttribute('stroke', 'currentColor');
      stem.setAttribute('stroke-width', '1.6');
      stem.setAttribute('stroke-linecap', 'round');
      svg.appendChild(stem);
      var dot = document.createElementNS(NS, 'circle');
      dot.setAttribute('cx', '12');
      dot.setAttribute('cy', '8');
      dot.setAttribute('r', '1');
      dot.setAttribute('fill', 'currentColor');
      svg.appendChild(dot);
      return svg;
    },

    region: function () {
      if (this._region && document.body.contains(this._region)) {
        return this._region;
      }
      var region = document.querySelector('.wishlist-toast-region');
      if (!region) {
        region = document.createElement('div');
        region.className = 'wishlist-toast-region';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        document.body.appendChild(region);
      }
      this._region = region;
      return region;
    },

    duration: function () {
      var ms = parseInt(WISHLIST_CONFIG.durationMs, 10);
      return isNaN(ms) || ms <= 0 ? 4000 : ms;
    },

    // Save toast: the product title (with a plain fallback) plus an "Ansehen"
    // action that opens the wishlist drawer.
    saved: function (payload) {
      if (WISHLIST_CONFIG.saveEnabled === false) {
        return;
      }
      var title = payload && payload.title ? payload.title : null;
      var image = payload && payload.image ? payload.image : null;
      this._show({
        variant: 'saved',
        text: title || WISHLIST_CONFIG.saveText || 'Artikel gespeichert',
        image: image,
        actionLabel: WISHLIST_CONFIG.savedActionLabel || 'Ansehen',
        onAction: function () {
          WishlistStore.openDrawer();
        }
      });
    },

    // Remove toast: a dark pill with the product thumbnail. Falls back to the info
    // glyph when no image is known (handled by _show), e.g. an item added and
    // removed in the same session before Swym returned its media.
    removed: function (payload) {
      if (WISHLIST_CONFIG.removeEnabled === false) {
        return;
      }
      this._show({
        variant: 'removed',
        text: WISHLIST_CONFIG.removeText || 'Artikel entfernt',
        image: payload && payload.image ? payload.image : null
      });
    },

    _show: function (options) {
      var region;
      try {
        region = this.region();
      } catch (error) {
        return; // a toast must never break the wishlist action
      }

      var toast = document.createElement('div');
      toast.className = 'wishlist-toast wishlist-toast--' + options.variant;

      // Product thumbnail when we have one (save toast), else the info glyph.
      if (options.image) {
        var thumb = document.createElement('img');
        thumb.className = 'wishlist-toast__thumb';
        thumb.src = options.image;
        thumb.alt = '';
        thumb.width = 40;
        thumb.height = 40;
        toast.appendChild(thumb);
      } else {
        var icon = document.createElement('span');
        icon.className = 'wishlist-toast__icon';
        icon.appendChild(this.infoIcon());
        toast.appendChild(icon);
      }

      var text = document.createElement('span');
      text.className = 'wishlist-toast__text';
      text.textContent = options.text;
      toast.appendChild(text);

      var durationMs = this.duration();
      var timer = null;
      var dismissed = false;

      function dismiss() {
        if (dismissed) {
          return;
        }
        dismissed = true;
        if (timer) {
          clearTimeout(timer);
        }
        toast.classList.add('wishlist-toast--leaving');
        var removeNode = function () {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast);
          }
        };
        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) {
          removeNode();
          return;
        }
        var done = false;
        var finish = function () {
          if (done) {
            return;
          }
          done = true;
          toast.removeEventListener('animationend', finish);
          removeNode();
        };
        toast.addEventListener('animationend', finish);
        setTimeout(finish, 400); // safety net if animationend never fires
      }

      if (options.actionLabel && typeof options.onAction === 'function') {
        var action = document.createElement('button');
        action.type = 'button';
        action.className = 'wishlist-toast__action';
        action.textContent = options.actionLabel;
        action.addEventListener('click', function () {
          options.onAction();
          dismiss();
        });
        toast.appendChild(action);
      }

      var progress = document.createElement('span');
      progress.className = 'wishlist-toast__progress';
      progress.style.setProperty('--wl-toast-duration', durationMs + 'ms');
      toast.appendChild(progress);

      region.appendChild(toast);

      // Pause the countdown while hovered so the action stays reachable.
      toast.addEventListener('mouseenter', function () {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        progress.style.animationPlayState = 'paused';
      });
      toast.addEventListener('mouseleave', function () {
        progress.style.animationPlayState = 'running';
        timer = setTimeout(dismiss, durationMs);
      });

      timer = setTimeout(dismiss, durationMs);
    }
  };

  // Central store. Buttons, the count badge and the drawer list subscribe to it.
  var WishlistStore = {
    ready: false,
    sdkTimedOut: false, // true once SDK_BOOT_TIMEOUT_MS passed without an SDK
    swat: null,
    productIds: new Set(), // empi values currently on the wishlist
    items: [], // raw Swym items: [{ epi, empi, du, dt, iu, pr }]
    listeners: new Set(),

    subscribe: function (fn) {
      this.listeners.add(fn);
      if (this.ready) {
        fn();
      }
      var self = this;
      return function () {
        self.listeners.delete(fn);
      };
    },

    notify: function () {
      this.listeners.forEach(function (fn) {
        try {
          fn();
        } catch (error) {
          // A single broken subscriber must not break the others.
        }
      });
    },

    has: function (productId) {
      return this.productIds.has(String(productId));
    },

    setItems: function (items) {
      this.items = Array.isArray(items) ? items : [];
      this.productIds = new Set(
        this.items.map(function (item) {
          return String(item.empi);
        })
      );
      this.notify();
    },

    // Merges a single item into the store. Driven by the Swym add event, which
    // also fires for our own optimistic adds, so it must be idempotent.
    applyAdd: function (item) {
      if (!item || item.empi == null) {
        return;
      }
      var empiKey = String(item.empi);
      var changed = false;
      if (!this.productIds.has(empiKey)) {
        this.productIds.add(empiKey);
        changed = true;
      }
      var inItems = this.items.some(function (existing) {
        return String(existing.empi) === empiKey;
      });
      if (!inItems) {
        this.items = this.items.concat([item]);
        changed = true;
      }
      if (changed) {
        this.notify();
      }
    },

    // Removes a product from the store. Idempotent mirror of applyAdd.
    applyRemove: function (empi) {
      if (empi == null) {
        return;
      }
      var empiKey = String(empi);
      var changed = this.productIds.delete(empiKey);
      var lengthBefore = this.items.length;
      this.items = this.items.filter(function (item) {
        return String(item.empi) !== empiKey;
      });
      if (changed || this.items.length !== lengthBefore) {
        this.notify();
      }
    },

    add: function (payload) {
      if (!this.swat) {
        reportError('add-no-sdk', new Error('Wishlist SDK not ready'), { empi: payload.empi });
        return;
      }
      // Only empi/epi/du go to Swym and the store; title/image are display-only
      // (used by the save notification) and must not pollute the stored item.
      var swymPayload = { empi: payload.empi, epi: payload.epi, du: payload.du };
      // Optimistic update for heart and drawer alike, reverted if the SDK call
      // fails. The payload (empi/epi/du) is all the drawer rendering needs.
      this.applyAdd(swymPayload);
      WishlistNotifications.saved(payload);
      try {
        this.swat.addToWishList(swymPayload, function () {}, function (error) {
          WishlistStore.applyRemove(swymPayload.empi);
          reportError('add-failed', error, { empi: swymPayload.empi });
        });
      } catch (error) {
        WishlistStore.applyRemove(swymPayload.empi);
        reportError('add-threw', error, { empi: swymPayload.empi });
      }
    },

    remove: function (payload) {
      if (!this.swat) {
        reportError('remove-no-sdk', new Error('Wishlist SDK not ready'), { empi: payload.empi });
        return;
      }
      var swymPayload = { empi: payload.empi, epi: payload.epi, du: payload.du };
      var empiKey = String(payload.empi);
      // Keep the removed rows so a failed SDK call can fully restore the state.
      var removedItems = this.items.filter(function (item) {
        return String(item.empi) === empiKey;
      });
      this.productIds.delete(empiKey);
      this.items = this.items.filter(function (item) {
        return String(item.empi) !== empiKey;
      });
      this.notify();
      // Thumbnail for the toast: the caller's image (card / drawer data attribute),
      // falling back to the removed Swym item's image url (iu).
      var removedImage =
        (payload && payload.image) || (removedItems[0] && removedItems[0].iu) || null;
      WishlistNotifications.removed({ image: removedImage });

      function revert() {
        WishlistStore.productIds.add(empiKey);
        WishlistStore.items = WishlistStore.items.concat(removedItems);
        WishlistStore.notify();
      }

      try {
        this.swat.removeFromWishList(swymPayload, function () {}, function (error) {
          revert();
          reportError('remove-failed', error, { empi: swymPayload.empi });
        });
      } catch (error) {
        revert();
        reportError('remove-threw', error, { empi: swymPayload.empi });
      }
    },

    refetch: function () {
      if (!this.swat) {
        return;
      }
      try {
        this.swat.fetch(
          function (items) {
            WishlistStore.setItems(items);
          },
          function (error) {
            reportError('fetch-failed', error);
          }
        );
      } catch (error) {
        reportError('fetch-threw', error);
      }
    },

    // Opens the wishlist drawer (used to surface the consent notice when the
    // SDK has not loaded because cookies were not accepted yet).
    openDrawer: function () {
      var drawer = document.getElementById('wishlist-drawer');
      if (!drawer || typeof drawer.show !== 'function') {
        return;
      }
      try {
        drawer.show();
      } catch (error) {
        reportError('open-drawer-failed', error);
      }
    },

    closeDrawer: function () {
      var drawer = document.getElementById('wishlist-drawer');
      if (drawer && typeof drawer.hide === 'function') {
        try {
          drawer.hide();
        } catch (error) {
          // Closing is best-effort.
        }
      }
    }
  };

  // Boot the SDK. All SDK access must go through SwymCallbacks.
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    WishlistStore.swat = swat;
    WishlistStore.ready = true;

    // Fail loud on a Swym SDK version mismatch (the methods we depend on).
    ['addToWishList', 'removeFromWishList', 'fetch'].forEach(function (method) {
      if (typeof swat[method] !== 'function') {
        reportError(
          'sdk-method-missing',
          new Error('swat.' + method + ' is not a function (Swym SDK version mismatch?)')
        );
      }
    });

    WishlistStore.refetch();

    // Keep the store in sync when items change (own calls and Swym UI alike).
    // Apply the event payload directly instead of refetching: right after an
    // add, swat.fetch still returns the previous (cached) list, which would
    // wipe the optimistic state and un-fill the heart until a second click.
    if (swat.evtLayer && typeof swat.evtLayer.addEventListener === 'function') {
      swat.evtLayer.addEventListener('sw:addedtowishlist', function (event) {
        var item = event && event.detail && event.detail.d;
        if (item && item.empi != null) {
          WishlistStore.applyAdd(item);
        } else {
          WishlistStore.refetch();
        }
      });
      swat.evtLayer.addEventListener('sw:removedfromwishlist', function (event) {
        var item = event && event.detail && event.detail.d;
        if (item && item.empi != null) {
          WishlistStore.applyRemove(item.empi);
        } else {
          WishlistStore.refetch();
        }
      });
    }

    WishlistStore.notify();
  });

  // The Swym SDK is consent-gated: without accepted cookies it never loads. That
  // is an expected state (the drawer shows a consent notice), not an error, so we
  // only leave a quiet debug trace. Flagging the timeout lets the drawer tell a
  // shopper who did consent that a blocker stops the wishlist.
  setTimeout(function () {
    if (!WishlistStore.ready) {
      WishlistStore.sdkTimedOut = true;
      logDebug('sdk-not-loaded', 'Swym SDK not initialized within 8s (consent missing or SDK blocked).');
      WishlistStore.notify();
    }
  }, SDK_BOOT_TIMEOUT_MS);

  // Exposed for the drawer markup and for debugging during development.
  // _internal holds the pure consent helpers for the unit tests.
  WishlistStore._internal = {
    pandectesFunctionalConsent: pandectesFunctionalConsent,
    drawerState: drawerState
  };
  window.ZahnheldWishlist = WishlistStore;

  // Swym wishlist reminder / retargeting emails link back with ?swaction=openui
  // to surface the saved items. This theme replaces Swym's default UI with a
  // custom drawer, so we intercept the parameter and open our drawer instead.
  // The drawer shows its loading / empty / consent state immediately and fills
  // in as the SDK loads, exactly like clicking the header heart.
  (function handleOpenUiParam() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('swaction') !== 'openui') {
        return;
      }
      // Strip the parameter so a refresh or back navigation does not reopen it.
      params.delete('swaction');
      var query = params.toString();
      var cleanUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
      window.history.replaceState(window.history.state, '', cleanUrl);

      // The drawer is an x-drawer custom element defined in theme.js (a module),
      // while this is a deferred classic script, so wait until it is registered
      // to guarantee .show() is available regardless of script load order.
      window.customElements.whenDefined('x-drawer').then(function () {
        WishlistStore.openDrawer();
      });
    } catch (error) {
      reportError('open-ui-param-failed', error);
    }
  })();

  // <wishlist-button> toggles a single product on the wishlist.
  var WishlistButton = class extends HTMLElement {
    connectedCallback() {
      this.control = this.querySelector('button') || this;
      this._onClick = this._onClick.bind(this);
      this._sync = this._sync.bind(this);
      this.addEventListener('click', this._onClick);
      this._unsubscribe = WishlistStore.subscribe(this._sync);
      this._sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this._unsubscribe) {
        this._unsubscribe();
      }
    }

    get payload() {
      return {
        empi: parseInt(this.getAttribute('data-wishlist-product-id'), 10),
        epi: parseInt(this.getAttribute('data-wishlist-variant-id'), 10),
        du: this.getAttribute('data-wishlist-url'),
        title: this.getAttribute('data-wishlist-title'),
        image: this.getAttribute('data-wishlist-image')
      };
    }

    get pressed() {
      return WishlistStore.has(this.getAttribute('data-wishlist-product-id'));
    }

    _sync() {
      var pressed = this.pressed;
      this.control.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      var label = pressed
        ? this.getAttribute('data-label-remove')
        : this.getAttribute('data-label-add');
      if (label) {
        this.control.setAttribute('aria-label', label);
      }
    }

    _onClick(event) {
      // The button often sits inside a product link, so stop the navigation.
      event.preventDefault();
      event.stopPropagation();
      if (!WishlistStore.ready) {
        // SDK not ready, so we cannot save yet. Open the drawer so the shopper
        // sees the right state: the cookie notice when consent is missing, or the
        // empty / loading state when consent is granted. Not an error.
        logDebug('click-before-ready', 'SDK not ready, opening drawer.');
        WishlistStore.openDrawer();
        return;
      }
      if (this.pressed) {
        WishlistStore.remove(this.payload);
      } else {
        this._playSaveAnimation();
        WishlistStore.add(this.payload);
      }
    }

    // Celebratory "saved" feedback: a one-shot heart beat plus a small burst of
    // mini-hearts fanning into the card. Called only from the add branch of
    // _onClick, so it never replays on page load or on remove. Skipped entirely
    // under prefers-reduced-motion. The CSS lives in assets/wishlist.css.
    _playSaveAnimation() {
      var control = this.control;
      if (!control) {
        return;
      }
      var reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        return;
      }

      // Beat the heart glyph once, then drop the class so it can play again next time.
      control.classList.add('is-saving');
      var clearBeat = function () {
        control.classList.remove('is-saving');
        control.removeEventListener('animationend', clearBeat);
      };
      control.addEventListener('animationend', clearBeat);
      setTimeout(clearBeat, 1000); // safety net if animationend never fires

      // Fan the mini-hearts into the card (angles 90deg = down .. 180deg = left) so
      // nothing is clipped at the pinned top-right corner.
      var PARTICLE_COUNT = 4;
      for (var i = 0; i < PARTICLE_COUNT; i++) {
        (function (index) {
          var angleRad = ((90 + Math.random() * 90) * Math.PI) / 180;
          var radius = 18 + Math.random() * 20;
          var tx = Math.cos(angleRad) * radius; // <= 0 (left)
          var ty = Math.sin(angleRad) * radius; // >= 0 (down)

          var particle = document.createElement('span');
          particle.className = 'wishlist-heart-particle';
          particle.style.setProperty('--tx', tx.toFixed(1) + 'px');
          particle.style.setProperty('--ty', ty.toFixed(1) + 'px');
          particle.style.animationDelay = index * 40 + 'ms';

          var remove = function () {
            if (particle.parentNode) {
              particle.parentNode.removeChild(particle);
            }
          };
          particle.addEventListener('animationend', remove);
          setTimeout(remove, 1200 + index * 40); // safety net

          control.appendChild(particle);
        })(i);
      }
    }
  };

  if (!window.customElements.get('wishlist-button')) {
    window.customElements.define('wishlist-button', WishlistButton);
  }

  // <wishlist-count> mirrors the number of saved products (header badge).
  var WishlistCount = class extends HTMLElement {
    connectedCallback() {
      this._sync = this._sync.bind(this);
      this._unsubscribe = WishlistStore.subscribe(this._sync);
      this._sync();
    }

    disconnectedCallback() {
      if (this._unsubscribe) {
        this._unsubscribe();
      }
    }

    _sync() {
      var count = WishlistStore.productIds.size;
      this.textContent = count;
      this.classList.toggle('opacity-0', count === 0);
    }
  };

  if (!window.customElements.get('wishlist-count')) {
    window.customElements.define('wishlist-count', WishlistCount);
  }

  // <wishlist-list> renders the drawer body: empty-state vs item rows.
  // Each row is the real theme line item, fetched via the Section Rendering API,
  // so it carries the sale badge, compare-at price and unit price like everywhere else.
  var WishlistList = class extends HTMLElement {
    connectedCallback() {
      this._sync = this._sync.bind(this);
      this._onListClick = this._onListClick.bind(this);
      this.consentEl = this.querySelector('[data-wishlist-consent]');
      this.blockedEl = this.querySelector('[data-wishlist-blocked]');
      this.noticeEl = document.querySelector('[data-wishlist-notice]');
      this.emptyEl = this.querySelector('[data-wishlist-empty]');
      this.listEl = this.querySelector('[data-wishlist-items]');
      this._renderedKey = null;
      if (this.listEl) {
        this.listEl.addEventListener('click', this._onListClick);
      }
      if (this.consentEl) {
        // Reopen the cookie banner (Pandectes #reopenBanner) and close the drawer
        // so the banner is visible. Do not preventDefault, the CMP needs the click.
        this.consentEl.addEventListener('click', function (event) {
          if (event.target.closest('a[href="#reopenBanner"]')) {
            WishlistStore.closeDrawer();
          }
        });
      }
      if (this.blockedEl) {
        // After allowing the shop in the blocker, only a reload brings the SDK.
        this.blockedEl.addEventListener('click', function (event) {
          if (event.target.closest('[data-wishlist-reload]')) {
            window.location.reload();
          }
        });
      }
      // Re-sync when the shopper makes a consent choice (Shopify-native event,
      // reliable even though Pandectes emits none) so the cookie notice clears
      // the moment consent is granted, without needing the SDK to have loaded.
      document.addEventListener('visitorConsentCollected', this._sync);
      this._unsubscribe = WishlistStore.subscribe(this._sync);
      this._sync();

      // The Customer Privacy API loads asynchronously and fires no event when it
      // becomes available (visitorConsentCollected only fires on a fresh consent
      // choice, not on reload). If consent is still unknown at connect time,
      // re-check a few times so an already-consented returning visitor is never
      // left on the cookie notice, even in the edge case where the SDK never boots.
      if (wishlistConsentGranted() === null) {
        var self = this;
        var attempts = 0;
        var recheck = function () {
          self._sync();
          attempts += 1;
          if (wishlistConsentGranted() === null && attempts < CONSENT_RECHECK_ATTEMPTS) {
            self._consentTimer = setTimeout(recheck, CONSENT_RECHECK_INTERVAL_MS);
          }
        };
        this._consentTimer = setTimeout(recheck, CONSENT_RECHECK_INTERVAL_MS);
      }
    }

    disconnectedCallback() {
      if (this.listEl) {
        this.listEl.removeEventListener('click', this._onListClick);
      }
      document.removeEventListener('visitorConsentCollected', this._sync);
      if (this._consentTimer) {
        clearTimeout(this._consentTimer);
      }
      if (this._unsubscribe) {
        this._unsubscribe();
      }
    }

    _sync() {
      var ready = WishlistStore.ready;
      var items = WishlistStore.items;
      var hasItems = ready && items.length > 0;

      // The cookie notice is only correct when consent is genuinely missing.
      // A shopper who has consented (per Shopify, or per Pandectes when a blocker
      // took out Shopify's consent API) is never asked again; see drawerState.
      var state = drawerState({
        ready: ready,
        hasItems: hasItems,
        shopifyConsent: wishlistConsentGranted(),
        pandectesConsent: pandectesFunctionalConsent(),
        sdkTimedOut: WishlistStore.sdkTimedOut
      });
      var showEmpty = state === 'empty';

      if (this.consentEl) {
        this.consentEl.hidden = state !== 'consent';
      }
      if (this.blockedEl) {
        this.blockedEl.hidden = state !== 'blocked';
      }
      // The guest session notice is meaningful only once the SDK is available.
      if (this.noticeEl) {
        this.noticeEl.hidden = !ready;
      }
      if (this.emptyEl) {
        this.emptyEl.hidden = !showEmpty;
      }
      if (this.listEl) {
        this.listEl.hidden = !hasItems;
        if (ready) {
          this._render(items);
        }
      }
    }

    _render(items) {
      var self = this;
      var key = items
        .map(function (item) {
          return item.empi;
        })
        .join(',');
      if (key === this._renderedKey) {
        return; // nothing changed, avoid refetching the sections
      }
      this._renderedKey = key;

      if (!items.length) {
        this.listEl.replaceChildren();
        return;
      }

      Promise.all(
        items.map(function (item) {
          return self._fetchLineItem(item);
        })
      ).then(function (nodes) {
        if (key !== self._renderedKey) {
          return; // a newer render started while we were fetching
        }
        self.listEl.replaceChildren.apply(self.listEl, nodes.filter(Boolean));
      }).catch(function (error) {
        reportError('render-failed', error);
      });
    }

    _fetchLineItem(item) {
      if (!item.du) {
        return Promise.resolve(null);
      }
      // Normalise to a same-origin request: take only the path and query of the
      // stored URL and resolve it against the current origin. This structurally
      // prevents any cross-origin fetch and also works on preview/myshopify
      // domains where shop.url (stored in du) differs from the current host.
      var url;
      try {
        var parsed = new URL(item.du, window.location.origin);
        parsed.searchParams.set('section_id', 'wishlist-line-item');
        url = parsed.pathname + parsed.search;
      } catch (error) {
        reportError('line-item-bad-url', error, { du: item.du });
        return Promise.resolve(null);
      }
      return fetch(url)
        .then(function (response) {
          if (!response.ok) {
            reportError('line-item-http', new Error('HTTP ' + response.status), { url: url });
            return '';
          }
          return response.text();
        })
        .then(function (html) {
          if (!html) {
            return null;
          }
          // Parse the same-origin section markup. DOMParser does not execute
          // scripts, and the content is our own theme HTML from the Section
          // Rendering API. Import only the inner row, the shopify-section
          // wrapper would duplicate its id across rows.
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var row = parsed.querySelector('.wishlist-line-item');
          if (!row) {
            reportError('line-item-empty', new Error('No .wishlist-line-item in section response'), { url: url });
          }
          return row ? document.importNode(row, true) : null;
        })
        .catch(function (error) {
          reportError('line-item-fetch', error, { url: url });
          return null;
        });
    }

    _onListClick(event) {
      var button = event.target.closest('.wishlist-line-item__remove');
      if (!button) {
        return;
      }
      event.preventDefault();

      var payload = {
        empi: parseInt(button.getAttribute('data-wishlist-product-id'), 10),
        epi: parseInt(button.getAttribute('data-wishlist-variant-id'), 10),
        du: button.getAttribute('data-wishlist-url'),
        image: button.getAttribute('data-wishlist-image')
      };

      var row = button.closest('.wishlist-line-item');

      // Ignore repeat clicks while this row's removal animation is already
      // running; the in-flight animation triggers the store removal itself.
      if (row && row.dataset.removing === 'true') {
        return;
      }

      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // No row or reduced motion: remove immediately without the animation.
      if (!row || reduceMotion) {
        WishlistStore.remove(payload);
        return;
      }

      this._animateRemoval(row, function () {
        WishlistStore.remove(payload);
      });
    }

    // Pop the row out before removing it from the store. Two stages so the
    // scale-up "pop" is not clipped: first a quick scale + fade with no overflow
    // change, then a collapse of the (now invisible) slot so the list closes.
    // WishlistStore.remove only re-renders once the store changes, so the exit
    // plays first; a failed SDK call reverts the store and re-renders the item.
    _animateRemoval(row, done) {
      row.dataset.removing = 'true';
      row.style.pointerEvents = 'none';

      var listGap = this.listEl ? parseFloat(getComputedStyle(this.listEl).rowGap) : 0;
      if (isNaN(listGap)) {
        listGap = 0;
      }

      var collapsed = false;
      var collapse = function () {
        if (collapsed) {
          return;
        }
        collapsed = true;
        row.removeEventListener('animationend', onPop);

        // Lock the layout height (transform does not affect offsetHeight), then
        // collapse the slot. The content is already invisible, so clipping is fine.
        row.style.height = row.offsetHeight + 'px';
        void row.offsetHeight;
        row.classList.remove('wishlist-line-item--popping');
        row.classList.add('wishlist-line-item--collapsing');
        row.style.height = '0px';
        row.style.paddingBlock = '0px';
        row.style.borderBlockWidth = '0px';
        // Pull the following item up by one list gap so the row leaves no empty slot.
        row.style.marginBlockEnd = '-' + listGap + 'px';

        var removed = false;
        var finish = function () {
          if (removed) {
            return;
          }
          removed = true;
          row.removeEventListener('transitionend', onCollapse);
          done();
        };
        var onCollapse = function (transitionEvent) {
          if (transitionEvent.target === row && transitionEvent.propertyName === 'height') {
            finish();
          }
        };
        row.addEventListener('transitionend', onCollapse);
        setTimeout(finish, 400); // safety net
      };

      var onPop = function (animationEvent) {
        if (animationEvent.target === row && animationEvent.animationName === 'wishlist-line-item-pop') {
          collapse();
        }
      };

      row.addEventListener('animationend', onPop);
      row.classList.add('wishlist-line-item--popping');
      setTimeout(collapse, 400); // safety net if animationend never fires
    }
  };

  if (!window.customElements.get('wishlist-list')) {
    window.customElements.define('wishlist-list', WishlistList);
  }
})();
